/**
 * Shared busy-events pipeline for both Manual and Self-schedule
 * calendars. Owns:
 *   - `/calendar_events` interviewer busy fetch
 *   - Visible-week filter + edit-mode "this interview" dedupe
 *   - Pastel-colored external events (manual shape — reused by both
 *     modes now that self-schedule renders via FullCalendar directly)
 *   - `BusyEvent[]` adapter for the availability algorithm
 *   - Meeting-room busy fetch + merge
 *
 * Manual-only overlays (available slots, no-slots popover, min-notice,
 * unavailable hours) stay in `CalendarPanel` — they assume a single
 * booked-slot workflow that self-schedule doesn't have.
 */
import { useMemo, useRef } from 'react';
import moment from 'moment-timezone';
import {
  useInterviewerCalendarChunksQuery,
  type PrefetchWeeks,
} from '@/api/calendarEvents';
import type { BusyEvent } from '../../availability';
import type { ScheduleSlot } from '../../state/useScheduleSharedState';
import { getPastelSlotColorsForUser } from '../interviewerPastelColors';
import { syncScheduleInterviewerPastelKeys } from '../../state/interviewerPastelMap';
import { useMeetingRoomBusyEvents } from './useMeetingRoomBusyEvents';
import type { ManualCalendarExternalEvent } from './useManualCalendarEvents';
import { isOwnInterviewBufferEvent } from '../events';

interface UseBusyCalendarEventsOptions {
  interviewerIds: number[];
  sortedInterviewerIds: number[];
  meetingRoomEmails: string[];
  meetingRoomNamesByEmail: Record<string, string>;
  visibleWeekStartIso: string;
  visibleWeekEndIso: string;
  resolvedTz: string;
  isLocalTimezone: boolean;
  atsJobId?: number;
  /**
   * Edit-mode only. Forwarded to `trigger_fetch` as `ats_interview_id`
   * so the BE flags THIS interview's own pre/post buffer events with
   * `buffer_kind` — the robust replacement for title-prefix matching.
   */
  atsInterviewId?: number;
  prefetchWeeks: PrefetchWeeks;
  lookaheadPrefetchWeeks: number;
  isEdit?: boolean;
  bookedSlot?: ScheduleSlot | null;
  interviewName?: string;
  originalInterviewName?: string;
  /**
   * The current interview's buffer minutes. When non-zero on an edit
   * with a booked slot, this interview's own pre/post buffer calendar
   * events are pulled out of the busy feed (rendered + algorithm) — the
   * clearance shows as the diagonal zone instead. See
   * `isOwnInterviewBufferEvent`.
   */
  bufferBeforeMinutes?: number;
  bufferAfterMinutes?: number;
  /**
   * How many weeks past the visible week start to include when
   * shaping busy events for the algorithm. Self-schedule feeds the
   * pipeline multi-week availability, so the busy events it subtracts
   * must span the same window. Manual mode only needs the visible
   * week. Defaults to 1 (visible week only).
   */
  algorithmWindowWeeks?: number;
}

export interface BusyCalendarEventsResult {
  /** Interviewer busy pastel events (manual shape). */
  interviewerExternalEvents: ManualCalendarExternalEvent[];
  /** Meeting-room busy pastel events (manual shape). */
  meetingRoomExternalEvents: ManualCalendarExternalEvent[];
  /** Combined interviewer + room events — consumed by both manual and self-schedule. */
  calendarExternalEvents: ManualCalendarExternalEvent[];
  /** Interviewer busy reshaped for the availability / slot algorithm. */
  interviewerBusyForAlgorithm: BusyEvent[];
  /** Combined interviewer + room busy for the algorithm. */
  busyEventsForAlgorithm: BusyEvent[];
  /** True while the visible-week fetch is still resolving. */
  isBlockingBusyFetch: boolean;
  /** True while the room-busy fetch is still resolving. */
  isRoomBusyBlocking: boolean;
  /** First fetch error (if any). */
  fetchError: Error | null;
  /**
   * Per-user metadata from the interviewer busy endpoint
   * (`calendar_available`, etc.) — surfaced so the form can render
   * "calendar not connected" warnings on chips.
   */
  usersInfoById: Record<string, { calendar_available?: boolean }>;
  /** True while ANY interviewer-calendar chunk is still fetching.
   *  Consumers must NOT emit "calendar not connected" until this is
   *  false — the SCH-40 sticky-true fold can only neutralize a bad
   *  per-window `calendar_available:false` once every chunk has
   *  resolved; emitting mid-flight surfaces a transient single-window
   *  false as a false positive. */
  isFetchingCalendarChunks: boolean;
}

export function useBusyCalendarEvents({
  interviewerIds,
  sortedInterviewerIds,
  meetingRoomEmails,
  meetingRoomNamesByEmail,
  visibleWeekStartIso,
  visibleWeekEndIso,
  resolvedTz,
  isLocalTimezone,
  atsJobId,
  atsInterviewId,
  prefetchWeeks,
  lookaheadPrefetchWeeks,
  isEdit,
  bookedSlot,
  interviewName,
  originalInterviewName,
  bufferBeforeMinutes = 0,
  bufferAfterMinutes = 0,
  algorithmWindowWeeks = 1,
}: UseBusyCalendarEventsOptions): BusyCalendarEventsResult {
  // Busy fetch must cover the algorithm window or self-schedule's slot
  // count drifts on week nav (cache freezes the no-busy-yet pipeline
  // output for weeks beyond `prefetchWeeks`; the next anchor catches up
  // and overwrites cache with the corrected, smaller count).
  const effectiveChunkPrefetchWeeks = Math.max(
    prefetchWeeks,
    Math.min(algorithmWindowWeeks, 4)
  ) as PrefetchWeeks;
  const {
    mergedEvents: calendarEvents,
    isBlockingBusyFetch,
    isFetching: isFetchingCalendarChunks,
    fetchError,
    usersInfoById,
  } = useInterviewerCalendarChunksQuery({
    userIds: interviewerIds,
    anchorWeekStartIso: visibleWeekStartIso,
    timezone: resolvedTz,
    prefetchWeeks: effectiveChunkPrefetchWeeks,
    lookaheadPrefetchWeeks,
    ...(atsJobId !== undefined && atsJobId !== null && atsJobId > 0
      ? { atsJobId }
      : {}),
    ...(atsInterviewId !== undefined &&
    atsInterviewId !== null &&
    atsInterviewId > 0
      ? { atsInterviewId }
      : {}),
    enabled: interviewerIds.length > 0,
    isLocalTimezone,
  });

  // Capture the SAVED booked-slot anchor once, the first time it's
  // available on an edit where buffer is active. The BE buffer events
  // are anchored to the saved booked time; matching them against the
  // LIVE booked slot would let them reappear as busy the instant the
  // recruiter drags the slot. We hide this interview's own buffer for
  // the whole session and let the BE reconcile positions on save.
  const ownBufferAnchorRef = useRef<{ startMs: number; endMs: number } | null>(
    null
  );
  if (
    isEdit &&
    ownBufferAnchorRef.current === null &&
    bookedSlot?.start &&
    bookedSlot?.end &&
    (bufferBeforeMinutes > 0 || bufferAfterMinutes > 0)
  ) {
    const s = moment(bookedSlot.start).valueOf();
    const e = moment(bookedSlot.end).valueOf();
    if (Number.isFinite(s) && Number.isFinite(e)) {
      ownBufferAnchorRef.current = { startMs: s, endMs: e };
    }
  }
  const ownBufferAnchor = ownBufferAnchorRef.current;

  // Window filter + edit-mode "this interview" dedupe.
  const calendarEventsForVisibleWeek = useMemo(() => {
    const vs = moment(visibleWeekStartIso).valueOf();
    const ve = moment(visibleWeekEndIso).valueOf();
    const editBookedStart =
      isEdit && bookedSlot?.start ? moment(bookedSlot.start).valueOf() : null;
    const editBookedEnd =
      isEdit && bookedSlot?.end ? moment(bookedSlot.end).valueOf() : null;
    return calendarEvents.filter((evt) => {
      const s = moment(evt.start).valueOf();
      const e = moment(evt.end).valueOf();
      if (!Number.isFinite(s) || !Number.isFinite(e)) return false;
      if (s >= ve || e <= vs) return false;
      // BE-flagged own pre/post buffer (edit mode, `ats_interview_id` sent).
      // Robust replacement for the title-anchor match below — surfaced as
      // the diagonal clearance zone instead of a busy block.
      if (evt.buffer_kind === 'pre' || evt.buffer_kind === 'post') return false;
      if (typeof evt.title !== 'string') return true;
      if (
        editBookedStart !== null &&
        editBookedEnd !== null &&
        s === editBookedStart &&
        e === editBookedEnd &&
        evt.title === interviewName
      ) {
        return false;
      }
      if (
        isEdit &&
        originalInterviewName &&
        evt.title === originalInterviewName
      ) {
        return false;
      }
      // This interview's own pre/post buffer events — surfaced instead
      // as the diagonal clearance zone. Matched against the SAVED anchor
      // (not the live slot) so they stay hidden when the slot is dragged.
      if (
        ownBufferAnchor &&
        isOwnInterviewBufferEvent(
          { title: evt.title, start: evt.start, end: evt.end },
          {
            bookedStartMs: ownBufferAnchor.startMs,
            bookedEndMs: ownBufferAnchor.endMs,
          }
        )
      ) {
        return false;
      }
      return true;
    });
  }, [
    calendarEvents,
    visibleWeekEndIso,
    visibleWeekStartIso,
    isEdit,
    bookedSlot,
    interviewName,
    originalInterviewName,
    ownBufferAnchor,
  ]);

  // Pastel-colored external events per interviewer.
  const interviewerExternalEvents = useMemo<
    ManualCalendarExternalEvent[]
  >(() => {
    syncScheduleInterviewerPastelKeys(sortedInterviewerIds);
    const seen = new Set<string>();
    const rows: ManualCalendarExternalEvent[] = [];
    for (const evt of calendarEventsForVisibleWeek) {
      const uidRaw = evt.user_id;
      const uid =
        typeof uidRaw === 'number' && Number.isFinite(uidRaw)
          ? uidRaw
          : sortedInterviewerIds[0];
      if (
        typeof uid === 'number' &&
        Number.isFinite(uid) &&
        !sortedInterviewerIds.includes(uid)
      ) {
        continue;
      }
      // `title` + `status` are part of the identity for the same reason as
      // the chunk-merge key in `useInterviewerCalendarChunksQuery` — BE rows
      // have no `id`, and a working-location "Home" (`free`) event shares its
      // exact bounds with an all-day OOO (`busy`) on the same day (ENG-4093).
      const dedupeKey = `${evt.id ?? ''}|${evt.start}|${evt.end}|${uid ?? ''}|${evt.title ?? ''}|${evt.status ?? ''}`;
      if (seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);
      const colors = getPastelSlotColorsForUser(sortedInterviewerIds, uid);
      rows.push({
        id: evt.id ?? `${evt.start}-${evt.end}-${uid ?? ''}`,
        start: evt.start,
        end: evt.end,
        title: evt.title ?? 'Busy',
        backgroundColor: colors.backgroundColor,
        // Border matches fill — legacy KInterviewScheduler parity.
        borderColor: colors.backgroundColor,
        textColor: colors.textColor,
        // Threaded so `useManualCalendarEvents` can flip free events
        // to a transparent / dashed-border ghost block. Algorithms
        // never see this — `interviewerBusyForAlgorithm` filters
        // `status === 'free'` upstream.
        status: evt.status,
      });
    }
    return rows;
  }, [calendarEventsForVisibleWeek, sortedInterviewerIds]);

  // Self-schedule feeds the pipeline multi-week availability, so the
  // busy events that subtract from it must span the same window. Defaults
  // to the visible week for manual mode.
  const algorithmWindowEndIso = useMemo(
    () =>
      moment(visibleWeekStartIso)
        .tz(resolvedTz)
        .add(algorithmWindowWeeks * 7, 'days')
        .toISOString(),
    [visibleWeekStartIso, resolvedTz, algorithmWindowWeeks]
  );

  const interviewerBusyForAlgorithm = useMemo<BusyEvent[]>(() => {
    const vs = moment(visibleWeekStartIso).valueOf();
    const ve = moment(algorithmWindowEndIso).valueOf();
    const editBookedStart =
      isEdit && bookedSlot?.start ? moment(bookedSlot.start).valueOf() : null;
    const editBookedEnd =
      isEdit && bookedSlot?.end ? moment(bookedSlot.end).valueOf() : null;
    const rows: BusyEvent[] = [];
    for (const evt of calendarEvents) {
      // `'free'` events are passed through the API allowlist so the
      // render layer can paint them as dotted ghost blocks, but they
      // must NOT contribute to slot subtraction / availability math.
      // This is the algorithm-side hard boundary — single point of
      // exclusion. Render-side stays in `interviewerExternalEvents`
      // above. See ALLOWED_EVENTS_TO_SHOW_IN_UI in calendarEventsApi.ts.
      if (evt.status === 'free') continue;
      const s = moment(evt.start).valueOf();
      const e = moment(evt.end).valueOf();
      if (!Number.isFinite(s) || !Number.isFinite(e)) continue;
      if (s >= ve || e <= vs) continue;
      // BE-flagged own buffer — must not subtract from this interview's
      // own availability (rendered as the clearance zone instead).
      if (evt.buffer_kind === 'pre' || evt.buffer_kind === 'post') continue;
      if (
        editBookedStart !== null &&
        editBookedEnd !== null &&
        s === editBookedStart &&
        e === editBookedEnd &&
        evt.title === interviewName
      ) {
        continue;
      }
      if (
        isEdit &&
        originalInterviewName &&
        evt.title === originalInterviewName
      ) {
        continue;
      }
      // Don't let this interview's own buffer blocks subtract from its
      // own availability (rendered as the clearance zone, not a
      // conflict). Matched against the SAVED anchor so a dragged slot
      // doesn't resurrect them.
      if (
        ownBufferAnchor &&
        isOwnInterviewBufferEvent(
          { title: evt.title, start: evt.start, end: evt.end },
          {
            bookedStartMs: ownBufferAnchor.startMs,
            bookedEndMs: ownBufferAnchor.endMs,
          }
        )
      ) {
        continue;
      }
      const uidRaw = evt.user_id;
      const hasValidUid = typeof uidRaw === 'number' && Number.isFinite(uidRaw);
      if (hasValidUid) {
        rows.push({
          id: String(evt.id ?? `${evt.start}-${evt.end}-${uidRaw}`),
          start: evt.start,
          end: evt.end,
          title: evt.title,
          interviewerId: uidRaw,
        });
        continue;
      }
      // No reliable `user_id` — fan out across every picked interviewer
      // so the per-user-union branch of the pipeline treats this as a
      // blocker for ALL of them. Attributing to `sortedInterviewerIds[0]`
      // alone (the previous fallback) made other interviewers appear
      // free at that time → slot offered on top of an unattributed
      // conflict. Flat-path math (panel / single-1:1) was incidentally
      // safe because it subtracts from common availability, but
      // per-user 1:1 multi underran. Defensive only — BE normally
      // emits `user_id` for interviewer events; this path rarely fires.
      if (sortedInterviewerIds.length === 0) continue;
      for (const uid of sortedInterviewerIds) {
        rows.push({
          id: String(
            evt.id ? `${evt.id}-${uid}` : `${evt.start}-${evt.end}-${uid}`
          ),
          start: evt.start,
          end: evt.end,
          title: evt.title,
          interviewerId: uid,
        });
      }
    }
    return rows;
  }, [
    calendarEvents,
    visibleWeekStartIso,
    algorithmWindowEndIso,
    sortedInterviewerIds,
    isEdit,
    bookedSlot,
    interviewName,
    originalInterviewName,
    ownBufferAnchor,
  ]);

  const {
    events: meetingRoomBusyEvents,
    externalEvents: meetingRoomExternalEvents,
    isBlockingBusyFetch: isRoomBusyBlocking,
  } = useMeetingRoomBusyEvents({
    roomEmails: meetingRoomEmails,
    roomNamesByEmail: meetingRoomNamesByEmail,
    visibleWeekStartIso,
    visibleWeekEndIso,
    timezone: resolvedTz,
    ...(atsJobId !== undefined ? { atsJobId } : {}),
    prefetchWeeks,
    lookaheadPrefetchWeeks,
    algorithmWindowWeeks,
    enabled: !isLocalTimezone && meetingRoomEmails.length > 0,
  });

  const busyEventsForAlgorithm = useMemo<BusyEvent[]>(
    () => [...interviewerBusyForAlgorithm, ...meetingRoomBusyEvents],
    [interviewerBusyForAlgorithm, meetingRoomBusyEvents]
  );

  const calendarExternalEvents = useMemo<ManualCalendarExternalEvent[]>(
    () => [...interviewerExternalEvents, ...meetingRoomExternalEvents],
    [interviewerExternalEvents, meetingRoomExternalEvents]
  );

  return {
    interviewerExternalEvents,
    meetingRoomExternalEvents,
    calendarExternalEvents,
    interviewerBusyForAlgorithm,
    busyEventsForAlgorithm,
    isBlockingBusyFetch,
    isRoomBusyBlocking,
    fetchError,
    usersInfoById,
    isFetchingCalendarChunks,
  };
}
